import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { Breadcrumb } from '@shared/chrome/Breadcrumb';
import { cn } from '@shared/ui/cn';
import { MailIcon } from '@shared/ui/icons';
import { DocumentIcon } from '@shared/ui/icons/nav';
import { MetaGrid } from '@shared/ui/Panel';

import { PAGES_ROUTES, PUBLIC_PATH, type PageSlug } from './constants';
import { sharedCopy } from './copy';

/**
 * The chrome both informational pages share.
 *
 * Everything here is composed from parts the design system already documents —
 * the breadcrumb of §9.15, the navigation entry of §9.14, the card of §9.5, the
 * definition list of §9.10, the alert of §9.4. **Nothing in this module is a new
 * component**, which is the same judgement the pack made when it carried each of
 * these on a data attribute rather than on a class it had invented.
 */

/**
 * The two-entry strip.
 *
 * §20.17 names both pages in one row, so the navigation column carries **one**
 * entry for the pair — and that leaves the second route with no inbound link at
 * all. This strip is what closes it: a route no surface links is a route that
 * does not exist for a reviewer.
 *
 * **The active indicator moves from the inline edge to the block edge**, and the
 * reason is the axis rather than taste. The column marks its current entry with
 * a rule at `border-inline-start`; in a horizontal strip that same rule reads as
 * a divider *between* two entries instead of a mark *on* one. Colour is still
 * not alone — `aria-current="page"` and the accent fill both hold.
 */
export function PagesStrip({ current }: { current: PageSlug }) {
  const entries = [
    { slug: 'about' as const, label: sharedCopy.strip.about, Glyph: DocumentIcon },
    { slug: 'contact' as const, label: sharedCopy.strip.contact, Glyph: MailIcon },
  ];

  return (
    <nav aria-label={sharedCopy.strip.label} className="mb-6 flex flex-wrap gap-2 border-b border-border">
      {entries.map(({ slug, label, Glyph }) => {
        const active = slug === current;

        return (
          <Link
            key={slug}
            to={PAGES_ROUTES[slug]}
            aria-current={active ? 'page' : undefined}
            className={cn(
              'hit-target -mb-px flex items-center gap-3 whitespace-nowrap rounded-t-sm border-b-2 px-3 py-2 text-sm no-underline',
              active
                ? 'border-b-accent bg-accent-soft font-semibold text-accent-text'
                : 'border-b-transparent text-ink-muted hover:bg-hover-bg hover:text-ink',
            )}
          >
            <Glyph />
            <span>{label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

/** The crumb and the head, in the order a reader needs them: where, then what. */
export function PageHead({ crumb, title, lede }: { crumb: string; title: string; lede: string }) {
  return (
    <>
      <Breadcrumb items={[{ label: sharedCopy.crumbs.console, to: '/' }, { label: crumb }]} />

      <div className="mb-6 mt-3 grid gap-stack-tight">
        <h1 className="font-display text-h1 font-bold">{title}</h1>
        <p className="mb-0 max-w-measure text-[1.0625rem] text-ink-muted">{lede}</p>
      </div>
    </>
  );
}

/**
 * A section — **flat, separated by the section gap**, not a panel.
 *
 * The pack draws these two surfaces as plain sections with a heading row and the
 * `--section-gap` between them, and that is what ships. The panel treatment
 * belongs to the `content/` pages, where a seven-section authoring form needed
 * elevation to stop reading as continuous prose; on a three-section page it
 * would be a shape this feature invented.
 */
export function PageSection({ id, heading, children }: { id: string; heading: string; children: ReactNode }) {
  return (
    <section aria-labelledby={id} className="[&+&]:mt-section-gap">
      <div className="mb-4">
        <h2 id={id} className="mb-0 font-display text-h2 font-bold">
          {heading}
        </h2>
      </div>
      {children}
    </section>
  );
}

/**
 * The page's identity — **facts, not fields.**
 *
 * §20.17 gives the operator the body content. The two informational pages are
 * two named surfaces rather than a collection that grows, so there is no create,
 * no delete and no rename here — and a title field would imply all three. Read
 * only, and it also answers «which of the two am I editing» without the operator
 * reading the breadcrumb back.
 *
 * The public path is `.mono` inside `<bdi>` on the **inner** element, never on
 * the `<dd>`: the class carries `direction: ltr`, and on the cell itself it
 * flips the grid track and the value lands against the far edge.
 */
export function PageIdentity({
  slug,
  title,
  bodyShape,
}: {
  slug: PageSlug;
  title: string;
  bodyShape: string;
}) {
  return (
    <MetaGrid>
      <dt>{sharedCopy.identity.page}</dt>
      <dd>{title}</dd>
      <dt>{sharedCopy.identity.path}</dt>
      <dd>
        <bdi className="mono">{PUBLIC_PATH[slug]}</bdi>
      </dd>
      <dt>{sharedCopy.identity.body}</dt>
      <dd>{bodyShape}</dd>
    </MetaGrid>
  );
}

/**
 * The action row.
 *
 * §9.2 order — the committing action first from the reading-start edge, then the
 * reset.
 *
 * **THE SAVE IS THE PRIMARY, AND IT IS RED.** It used to be drawn secondary on
 * the reasoning that the top bar's create action is the view's one primary — but
 * neither informational page HAS a create action, so the rule that permits one
 * red button per view was spending it on a button that does not exist and
 * leaving the page's only committing action indistinguishable from its reset.
 * §9.1 caps the count at one; it does not require the count to be zero. Nothing
 * else on either page is red, so the cap still holds.
 *
 * From the desktop tier it becomes the form's footer: a rule above it and a
 * wider gap, so the two buttons read as the end of the whole form rather than as
 * two more actions belonging to the section immediately above them.
 */
export function FormActions({
  saving,
  onReset,
  saveLabel,
  resetLabel,
}: {
  saving: boolean;
  onReset: () => void;
  saveLabel: string;
  resetLabel: string;
}) {
  return (
    <div className="mt-4 flex flex-wrap gap-3 lg:mt-8 lg:border-t lg:border-border lg:pt-5">
      <button
        type="submit"
        disabled={saving}
        className="hit-target rounded-sm border border-accent bg-accent px-5 text-sm font-semibold text-accent-ink hover:bg-accent-hover active:bg-accent-press disabled:cursor-not-allowed disabled:opacity-45"
      >
        {saveLabel}
      </button>
      <button
        type="button"
        onClick={onReset}
        className="hit-target rounded-sm border border-transparent px-5 text-sm font-semibold text-ink-muted hover:bg-hover-bg hover:text-ink"
      >
        {resetLabel}
      </button>
    </div>
  );
}

/** The label-over-control field both pages use for prose. */
export function WideField({
  id,
  label,
  hint,
  hintId,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  hintId?: string;
  children: ReactNode;
}) {
  return (
    <div className="mb-4">
      <label htmlFor={id} className="mb-2 block text-sm font-medium">
        {label}
      </label>
      {children}
      {hint ? (
        <p id={hintId} className="mb-0 mt-2 text-xs text-ink-muted">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/**
 * The control classes, in one place.
 *
 * A `.field` stops at 30 rem, which is the measure of a field filled with one
 * line. A body being written is not one, so the controls that hold prose take
 * the full column and the short identifiers keep the cap.
 */
export const CONTROL_CLASS =
  'min-h-[2.75rem] w-full rounded-sm border border-border-strong bg-surface-raised px-4 py-3 text-ink [font-size:var(--fs-body)] [line-height:normal] hover:border-ink-muted focus-visible:border-accent';

export const TEXTAREA_CLASS =
  'w-full rounded-sm border border-border-strong bg-surface-raised px-4 py-3 text-ink [font-size:var(--fs-body)] [resize:vertical] hover:border-ink-muted focus-visible:border-accent';

/** Short, machine-shaped identifiers keep the field measure and read left to right. */
export const IDENTIFIER_CLASS = cn(CONTROL_CLASS, 'mono max-w-[30rem]');
